import React from 'react';
import { formatCurrency } from 'utils';
import { Info } from '@mui/icons-material';
import { Column } from 'components/shared/TableListing';
import { Stack, Tooltip, Typography } from '@mui/material';

import { CloseDateLegend } from './CloseDateLegend';
import { DealStatusLegend } from './DealStatusLegend';
import { CloseDateCell, CustomerCell, DealStatusDotCell, StageCell, CreatedCell } from './cells';
import { BPFDealStatus, ReportsListingContext, ReportsListingViewModel } from '../../types';

const ROW_TINT: Record<BPFDealStatus, string> = {
  [BPFDealStatus.Healthy]: 'white',
  [BPFDealStatus.Hot]: '#FDF2F8',
  [BPFDealStatus.Stalled]: '#FFFBEB',
  [BPFDealStatus.AtRisk]: '#FEF3F2',
};

const getRowBg = (row: ReportsListingViewModel) => ROW_TINT[row.dealStatus] ?? 'white';

export function getReportsListingConfig(): Column<ReportsListingViewModel, ReportsListingContext>[] {
  return [
    {
      width: '6rem',
      header: 'Status',
      footerColSpan: 4,
      renderKey: 'dealStatus',
      bodyStyles: ({ row }) => ({ backgroundColor: getRowBg(row) }),
      renderCell: ({ row }) => <DealStatusDotCell row={row} />,
      renderHeadCell: () => (
        <Stack direction="row" gap="0.2rem" alignItems="center">
          <Tooltip arrow placement="bottom-start" title={<DealStatusLegend />}>
            <Info color="primary" fontSize="small" />
          </Tooltip>

          <Typography variant="subtitle2" component="span">
            Status
          </Typography>
        </Stack>
      ),
      renderFooter: () => (
        <Typography variant="subtitle1" fontWeight={500} component="span">
          Grand total
        </Typography>
      ),
    },
    {
      width: '15rem',
      header: 'Customer',
      renderKey: 'customer',
      bodyStyles: ({ row }) => ({ backgroundColor: getRowBg(row) }),
      renderCell: ({ row }) => <CustomerCell row={row} />,
    },
    {
      width: '15rem',
      header: 'Close Date',
      renderKey: 'closeDate',
      bodyStyles: ({ row }) => ({ backgroundColor: getRowBg(row) }),
      renderCell: ({ row }) => <CloseDateCell row={row} />,
      renderHeadCell: () => (
        <Stack direction="row" gap="0.2rem" alignItems="center">
          <Tooltip arrow placement="bottom-start" title={<CloseDateLegend />}>
            <Info color="primary" fontSize="small" />
          </Tooltip>

          <Typography variant="subtitle2" component="span">
            Close Date
          </Typography>
        </Stack>
      ),
    },
    {
      width: '8rem',
      header: 'Location',
      renderKey: 'location',
      bodyStyles: ({ row }) => ({ backgroundColor: getRowBg(row) }),
      renderCell: ({ row }) => <Typography>{row.location || 'N/A'}</Typography>,
    },
    {
      width: '9rem',
      header: 'Invoice Amount',
      renderKey: 'invoiceAmount',
      bodyStyles: ({ row }) => ({ backgroundColor: getRowBg(row) }),
      renderCell: ({ row }) => (
        <Stack width="100%">
          <Typography align="center">{row.invoiceAmount ? formatCurrency(row.invoiceAmount) : 'N/A'}</Typography>
        </Stack>
      ),
      renderFooter: ({ context: { totals } }) => <Typography>{formatCurrency(totals.invoiceAmount, true)}</Typography>,
    },
    {
      width: '8rem',
      header: 'Gross Profit',
      renderKey: 'grossProfit',
      bodyStyles: ({ row }) => ({ backgroundColor: getRowBg(row) }),
      renderCell: ({ row }) => (
        <Stack width="100%">
          <Typography align="center">
            {row.grossProfit !== null ? formatCurrency(row.grossProfit, true) : 'N/A'}
          </Typography>
        </Stack>
      ),
      renderFooter: ({ context: { totals } }) => <Typography>{formatCurrency(totals.weightedAmount, true)}</Typography>,
      footerColSpan: 2,
    },
    {
      width: '9rem',
      header: 'Probability %',
      renderKey: 'probability',
      renderHeadCell: () => (
        <Stack direction="row" gap="0.5rem" alignItems="center">
          <Tooltip placement="top" arrow title="Best VO or Stage probability">
            <Info color="primary" fontSize="small" />
          </Tooltip>

          <Typography variant="subtitle2" component="span">
            Probability %
          </Typography>
        </Stack>
      ),
      bodyStyles: ({ row }) => ({ backgroundColor: getRowBg(row) }),
      renderCell: ({ row }) => (
        <Stack width="100%">
          <Typography align="center">{row.probability ? `${row.probability}%` : 'N/A'}</Typography>
        </Stack>
      ),
    },
    {
      width: '11rem',
      footerColSpan: 3,
      header: 'Weighted Amount',
      renderKey: 'weightedAmount',
      bodyStyles: ({ row }) => ({ backgroundColor: getRowBg(row) }),
      renderHeadCell: () => (
        <Stack direction="row" gap="0.5rem" alignItems="center">
          <Tooltip placement="top" arrow title="Weighted Amount = Invoice Total x Probability %">
            <Info color="primary" fontSize="small" />
          </Tooltip>
          <Typography variant="subtitle2" component="span">
            Weighted Amount
          </Typography>
        </Stack>
      ),
      renderCell: ({ row }) => (
        <Stack width="100%">
          <Typography align="center">
            {row.weightedAmount !== null ? formatCurrency(row.weightedAmount, true) : 'N/A'}
          </Typography>
        </Stack>
      ),
      renderFooter: ({ context: { totals } }) => <Typography>{formatCurrency(totals.weightedAmount, true)}</Typography>,
    },
    {
      header: 'Stage',
      renderKey: 'stage',
      bodyStyles: ({ row }) => ({ backgroundColor: getRowBg(row) }),
      renderCell: ({ row }) => <StageCell row={row} />,
    },
    {
      width: '11rem',
      header: 'Created',
      renderKey: 'createdDateTime',
      bodyStyles: ({ row }) => ({ backgroundColor: getRowBg(row) }),
      renderCell: ({ row }) => <CreatedCell row={row} />,
    },
  ];
}
